import logger from '../../utils/logger';
import storage from '../../utils/LocalStorageManager';
import { PREFERRED_ICON_TYPE, TypeConverters } from '../../config/TypeConfig';

/**
 * Dock工具类 - 处理Dock相关的业务逻辑
 */
class DockUtils {
  /**
   * 规范化应用数据
   * @param {Object} app - 原始应用数据
   * @returns {Object} 规范化后的应用数据
   */
  static normalizeApp(app) {
    if (!app || !app.id) {
      logger.warn('[DockUtils] 应用数据无效');
      return null;
    }
    
    return {
      id: app.id,
      title: app.title || app.name || '未命名应用',
      url: app.url || app.link || '#',
      ico: app.ico || app.icon || null,
      iconUrl: app.iconUrl || app.ico || app.icon || null,
      bgColor: app.bgColor || 'white',
      link: app.link || app.url || '#',
      icon: app.icon || app.ico || null,
      textIcon: app.textIcon || null,
      preferredIconType: TypeConverters.convertIconType(app.preferredIconType, app),
      iconColor: app.iconColor || 'white',
      // 如果没有图片图标也没有文字图标，从标题创建文字图标
      ...((!app.ico && !app.icon && !app.iconUrl && !app.textIcon) ? { 
        textIcon: (app.title || app.name || '未').substring(0, 4),
        preferredIconType: PREFERRED_ICON_TYPE.TEXT
      } : {})
    };
  }

  /**
   * 添加应用到最近使用
   * @param {Object} app - 应用数据
   */
  static addToRecentApps(app) {
    try {
      const normalizedApp = this.normalizeApp(app);
      if (!normalizedApp) return false;

      const storedApps = storage.getDockRecentApps() || [];
      let recentApps = [...storedApps];
      
      // 如果已存在，先移除
      recentApps = recentApps.filter(item => item.id !== normalizedApp.id);
      
      // 添加到头部并限制数量
      recentApps = [normalizedApp, ...recentApps].slice(0, 3);
      
      // 保存回localStorage
      storage.setDockRecentApps(recentApps);
      
      // 触发事件
      const event = new CustomEvent('dockAddRecentApp', {
        detail: normalizedApp
      });
      window.dispatchEvent(event);
      
      logger.log('[DockUtils] 添加到最近使用成功');
      return true;
    } catch (error) {
      logger.error('[DockUtils] 添加到最近使用失败:', error);
      return false;
    }
  }

  /**
   * 获取最近使用的应用
   * @returns {Array} 最近使用的应用列表
   */
  static getRecentApps() {
    try {
      const apps = storage.getDockRecentApps() || [];
      return apps.filter(app => app); // 过滤掉null/undefined
    } catch (error) {
      logger.error('[DockUtils] 获取最近使用应用失败:', error);
      return [];
    }
  }

  /**
   * 获取最常用的网站
   * @param {number} limit - 数量限制
   * @returns {Array} 最常用网站列表
   */
  static getMostUsedWebsites(limit = 3) {
    try {
      return storage.getMostUsedWebsites(limit);
    } catch (error) {
      logger.error('[DockUtils] 获取最常用网站失败:', error);
      return [];
    }
  }

  /**
   * 记录网站点击
   * @param {string} url - 网站URL
   * @param {Object} websiteData - 网站数据
   */
  static recordWebsiteClick(url, websiteData) {
    console.log('[DockUtils] recordWebsiteClick 被调用', { url, websiteData });
    try {
      if (!url) {
        logger.warn('[DockUtils] URL为空，无法记录点击');
        return false;
      }
      
      const success = storage.recordWebsiteClick(url, websiteData);
      if (success) {
        // 触发更新事件
        const event = new CustomEvent('dockUpdateMostUsed');
        window.dispatchEvent(event);
      }
      return success;
    } catch (error) {
      logger.error('[DockUtils] 记录网站点击失败:', error);
      return false;
    }
  }

  /**
   * 从最近使用中移除应用
   * @param {string} appId - 应用ID
   */
  static removeFromRecentApps(appId) {
    try {
      const storedApps = storage.getDockRecentApps() || [];
      const newApps = storedApps.filter(app => app && app.id !== appId);
      
      if (newApps.length !== storedApps.length) {
        storage.setDockRecentApps(newApps);
        logger.log(`[DockUtils] 从最近使用中移除应用: ${appId}`);
      }
      
      return newApps;
    } catch (error) {
      logger.error('[DockUtils] 从最近使用中移除应用失败:', error);
      return [];
    }
  }

  /**
   * 处理图标加载错误
   * @param {string} appId - 应用ID
   * @param {Object} app - 应用数据
   * @returns {Object} 更新后的应用数据
   */
  static handleIconLoadError(appId, app) {
    try {
      if (!app || app.id !== appId) return app;
      
      // 如果应用没有文字图标，则从应用名称中提取
      let textIcon = app.textIcon;
      if (!textIcon) {
        textIcon = (app.title || app.name || '未').substring(0, 4);
      }
      
      return {
        ...app,
        // 清除图片图标相关属性
        iconUrl: null,
        ico: null,
        // 确保设置了文字图标
        textIcon: textIcon,
        // 设置首选图标类型为"text"，强制使用文字图标
        preferredIconType: PREFERRED_ICON_TYPE.TEXT
      };
    } catch (error) {
      logger.error('[DockUtils] 处理图标加载错误失败:', error);
      return app;
    }
  }

  /**
   * 清理过期的点击记录
   * @param {number} daysToKeep - 保留天数
   */
  static cleanupExpiredRecords(daysToKeep = 30) {
    try {
      const removedCount = storage.clearExpiredClickRecords(daysToKeep);
      if (removedCount > 0) {
        // 触发更新事件
        const event = new CustomEvent('dockUpdateMostUsed');
        window.dispatchEvent(event);
        logger.log(`[DockUtils] 清理过期记录: ${removedCount}个`);
      }
      return removedCount;
    } catch (error) {
      logger.error('[DockUtils] 清理过期记录失败:', error);
      return 0;
    }
  }
}

export default DockUtils;
